if (!customElements.get('list-card-product')) {
  customElements.define('list-card-product', class ListCardProduct extends HTMLElement {
    constructor() {
      super();

      this.productVariant = false;
      this.cartNotification = document.querySelector("cart-notification");
      this.mql = window.matchMedia('(min-width: 768px)');
      this.eventMouseEnterHappened = false;
      
      this.bindEvents();
      this.addAccessibilityAttributes();
      this.initializeVariantHandling();
      this.initializeWishlistHandling();
      this.initializeWishlistButtonHandling();
      this.initializeWishlistState();

      this.onClickAwayHandler = this.onClickAway.bind(this);
      this.onMediaQueryChangeHandler = this.onMediaQueryChange.bind(this);
    }

    connectedCallback() {
      document.addEventListener("click", this.onClickAwayHandler);
      this.mql.addEventListener('change', this.onMediaQueryChangeHandler);
      
      // Listen for wishlist updates from modal
      this.wishlistItemAddedHandler = (event) => {
        const { productId, variantId } = event.detail;
        // Check if this is our product and update state
        const ourVariantButtons = this.querySelectorAll('.backup-addToWishlist');
        ourVariantButtons.forEach(btn => {
          if (btn.dataset.productId == productId && btn.dataset.variantId == variantId) {
            this.fetchAndUpdateWishlistState();
          }
        });
      };
      document.addEventListener('wishlistItemAdded', this.wishlistItemAddedHandler);
      
      // Re-initialize handlers when connected to ensure proper setup
      this.initializeVariantHandling();
      this.initializeWishlistHandling();
      this.initializeWishlistButtonHandling();
      this.initializeWishlistState();
    }

    disconnectedCallback() {
      document.removeEventListener("click", this.onClickAwayHandler);
      this.mql.removeEventListener('change', this.onMediaQueryChangeHandler);
      
      // Remove wishlist update listener
      if (this.wishlistItemAddedHandler) {
        document.removeEventListener('wishlistItemAdded', this.wishlistItemAddedHandler);
      }
      
      // Clean up any remaining event listeners
      this.cleanupEventListeners();
    }

    onMediaQueryChange() {
      // Re-initialize handlers when media query changes
      this.initializeVariantHandling();
      this.initializeWishlistHandling();
      this.initializeWishlistButtonHandling();
    }

    cleanupEventListeners() {
      // Remove any dynamic event listeners to prevent memory leaks
      const modals = this.querySelectorAll('.cartModal, .wishModal');
      modals.forEach(modal => {
        modal.classList.remove('open');
      });
      
      if (document.body.classList.contains('overflow-hidden')) {
        document.body.classList.remove('overflow-hidden');
      }
    }

    bindEvents() {
      this.querySelectorAll(".list-card-product__listbox a").forEach((anchor) => {
        anchor.addEventListener("click", this.onClick.bind(this));
      });
      
      const checker = this.querySelector('[name="checker"]');
      if (checker) {
        checker.addEventListener("change", this.onChange.bind(this));
      }

      this.addEventListener('click', this.handleAddToCart.bind(this));
      this.addEventListener('click', this.handleWishlistClick.bind(this));
    }

    addAccessibilityAttributes() {
      this.querySelectorAll(".list-card-product__listbox a").forEach((anchor) => {
        anchor.setAttribute("role", "button");
      });
    }

    initializeVariantHandling() {
      const productCard = this.querySelector('.list-card-product');
      if (!productCard || !productCard.classList.contains('hasVariants')) return;

      const productCTA = this.querySelector('.cartContainer');
      const productMedia = this.querySelector('.mediaWrapper');
      const cartModal = this.querySelector('.cartModal');

      if (!cartModal) return;

      // Clean up existing listeners
      this.cleanupVariantListeners();

      if (this.mql.matches) {
        // Desktop behavior - hover
        [productCTA].forEach(element => {
          if (element) {
            const mouseEnterHandler = () => {
              this.eventMouseEnterHappened = true;
              cartModal.classList.add('open');
            };
            
            const mouseLeaveHandler = () => {
              this.eventMouseEnterHappened = false;
              cartModal.classList.remove('open');
            };

            element.addEventListener('mouseenter', mouseEnterHandler);
            element.addEventListener('mouseleave', mouseLeaveHandler);
            
            // Store handlers for cleanup
            element._mouseEnterHandler = mouseEnterHandler;
            element._mouseLeaveHandler = mouseLeaveHandler;
          }
        });
      } else {
        // Mobile behavior - click
        [productCTA].forEach(element => {
          if (element) {
            const backupVariants = element.querySelector('.backup-hasVariants');
            const closeButton = element.querySelector('button.dialogCloseButton');
            
            if (backupVariants) {
              const clickHandler = (e) => {
                e.preventDefault();
                e.stopPropagation();
                cartModal.classList.add('open');
                document.body.classList.add('overflow-hidden');
              };
              
              backupVariants.addEventListener('click', clickHandler);
              backupVariants._clickHandler = clickHandler;
            }
            
            if (closeButton) {
              const closeHandler = (e) => {
                e.preventDefault();
                e.stopPropagation();
                cartModal.classList.remove('open');
                document.body.classList.remove('overflow-hidden');
              };
              
              closeButton.addEventListener('click', closeHandler);
              closeButton._closeHandler = closeHandler;
            }
          }
        });
      }
    }

    cleanupVariantListeners() {
      const productCTA = this.querySelector('.cartContainer');
      const productMedia = this.querySelector('.mediaWrapper');
      
      [productCTA].forEach(element => {
        if (element) {
          // Remove desktop listeners
          if (element._mouseEnterHandler) {
            element.removeEventListener('mouseenter', element._mouseEnterHandler);
            delete element._mouseEnterHandler;
          }
          if (element._mouseLeaveHandler) {
            element.removeEventListener('mouseleave', element._mouseLeaveHandler);
            delete element._mouseLeaveHandler;
          }
          
          // Remove mobile listeners
          const backupVariants = element.querySelector('.backup-hasVariants');
          const closeButton = element.querySelector('button.dialogCloseButton');
          
          if (backupVariants && backupVariants._clickHandler) {
            backupVariants.removeEventListener('click', backupVariants._clickHandler);
            delete backupVariants._clickHandler;
          }
          
          if (closeButton && closeButton._closeHandler) {
            closeButton.removeEventListener('click', closeButton._closeHandler);
            delete closeButton._closeHandler;
          }
        }
      });
    }

    initializeWishlistHandling() {
      const productCard = this.querySelector('.list-card-product');
      if (!productCard) return;

      const wishButton = this.querySelector('button.wishlistButton');
      if (!wishButton) return;

      // Get product data from button or card
      this.productData = this.getProductData();
      if (!this.productData) return;

      // Clean up existing listeners
      this.cleanupWishlistListeners();

      // Check if this is a single variant product (has backup-addToWishlist class on main button)
      const isSingleVariant = wishButton.classList.contains('backup-addToWishlist');

      if (this.mql.matches) {
        // Desktop behavior - hover
        const localModal = this.querySelector('.wishModal');
        const wishDialog = this.querySelector('.wishDialog');
        
        if (localModal && wishDialog) {
          // Use local modal for desktop hover (if it exists)
          [wishButton, wishDialog].forEach(element => {
            if (element) {
              const mouseEnterHandler = () => {
                this.eventMouseEnterHappened = true;
                localModal.classList.add('open');
                wishDialog?.classList.add('open');
              };
              
              const mouseLeaveHandler = () => {
                this.eventMouseEnterHappened = false;
                localModal.classList.remove('open');
                wishDialog?.classList.remove('open');
              };

              element.addEventListener('mouseenter', mouseEnterHandler);
              element.addEventListener('mouseleave', mouseLeaveHandler);
              
              element._wishMouseEnterHandler = mouseEnterHandler;
              element._wishMouseLeaveHandler = mouseLeaveHandler;
            }
          });
        }
      } else {
        // Mobile behavior - click, use shared modal
        if (wishButton && !isSingleVariant) {
          // For multi-variant products, show shared modal on click
          const clickHandler = (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.openSharedWishlistModal();
          };
          
          wishButton.addEventListener('click', clickHandler);
          wishButton._wishClickHandler = clickHandler;
        }
        // Note: Single variant products will be handled by the backup-addToWishlist class
        // through the existing handleWishlistClick method
      }
    }

    getProductData() {
      // Try to get product data from the wishlist button first
      const wishButton = this.querySelector('button.wishlistButton');
      if (wishButton && wishButton.dataset.productId) {
        return {
          productId: wishButton.dataset.productId,
          handle: wishButton.dataset.handle,
          title: wishButton.dataset.productTitle,
          image: wishButton.dataset.image,
          variantId: wishButton.dataset.variantId,
          price: wishButton.dataset.price,
          variantTitle: wishButton.dataset.variantTitle
        };
      }

      // Try to extract from rendered wishlistModal if it exists
      const localModal = this.querySelector('.wishModal');
      if (localModal) {
        const variantButtons = localModal.querySelectorAll('.backup-addToWishlist');
        if (variantButtons.length > 0) {
          const firstButton = variantButtons[0];
          return {
            productId: firstButton.dataset.productId,
            handle: firstButton.dataset.handle,
            title: firstButton.dataset.productTitle,
            image: firstButton.dataset.image,
            variants: Array.from(variantButtons).map(btn => ({
              id: btn.dataset.variantId,
              title: btn.dataset.variantTitle || btn.textContent.trim(),
              price: btn.dataset.price,
              productId: btn.dataset.productId,
              handle: btn.dataset.handle,
              productTitle: btn.dataset.productTitle,
              image: btn.dataset.image
            }))
          };
        }
      }

      return null;
    }

    cleanupWishlistListeners() {
      const wishButton = this.querySelector('button.wishlistButton');
      const wishDialog = this.querySelector('.wishDialog');
      const wishModalClose = this.querySelector('.wishModal button.dialogCloseButton');
      const backupWishButton = this.querySelector('button.backup-hasVariants-wishlist');
      
      [wishButton, wishDialog].forEach(element => {
        if (element) {
          if (element._wishMouseEnterHandler) {
            element.removeEventListener('mouseenter', element._wishMouseEnterHandler);
            delete element._wishMouseEnterHandler;
          }
          if (element._wishMouseLeaveHandler) {
            element.removeEventListener('mouseleave', element._wishMouseLeaveHandler);
            delete element._wishMouseLeaveHandler;
          }
        }
      });
      
      if (wishButton && wishButton._wishClickHandler) {
        wishButton.removeEventListener('click', wishButton._wishClickHandler);
        delete wishButton._wishClickHandler;
      }
      
      if (wishModalClose && wishModalClose._wishCloseHandler) {
        wishModalClose.removeEventListener('click', wishModalClose._wishCloseHandler);
        delete wishModalClose._wishCloseHandler;
      }
      
      if (backupWishButton && backupWishButton._backupWishClickHandler) {
        backupWishButton.removeEventListener('click', backupWishButton._backupWishClickHandler);
        delete backupWishButton._backupWishClickHandler;
      }
    }

    initializeWishlistButtonHandling() {
      const productCard = this.querySelector('.list-card-product');
      if (!productCard) return;

      const productCTA = this.querySelector('.cartContainer');
      const wishlistButton = productCTA?.querySelector('.backupWishlistButton.backup-hasVariants');
      const cartModal = this.querySelector('.cartModal');

      if (!wishlistButton || !cartModal) return;

      // Clean up existing listeners
      this.cleanupWishlistButtonListeners();

      if (this.mql.matches) {
        // Desktop behavior - hover
        const mouseEnterHandler = () => {
          this.eventMouseEnterHappened = true;
          cartModal.classList.add('open');
        };
        
        const mouseLeaveHandler = () => {
          this.eventMouseEnterHappened = false;
          cartModal.classList.remove('open');
        };

        productCTA.addEventListener('mouseenter', mouseEnterHandler);
        productCTA.addEventListener('mouseleave', mouseLeaveHandler);
        
        // Store handlers for cleanup
        productCTA._wishlistMouseEnterHandler = mouseEnterHandler;
        productCTA._wishlistMouseLeaveHandler = mouseLeaveHandler;
      } else {
        // Mobile behavior - click
        const clickHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          cartModal.classList.add('open');
          document.body.classList.add('overflow-hidden');
        };
        
        wishlistButton.addEventListener('click', clickHandler);
        wishlistButton._wishlistClickHandler = clickHandler;
        
        const closeButton = cartModal.querySelector('button.dialogCloseButton');
        if (closeButton) {
          const closeHandler = (e) => {
            e.preventDefault();
            e.stopPropagation();
            cartModal.classList.remove('open');
            document.body.classList.remove('overflow-hidden');
          };
          
          closeButton.addEventListener('click', closeHandler);
          closeButton._wishlistCloseHandler = closeHandler;
        }
      }
    }

    cleanupWishlistButtonListeners() {
      const productCTA = this.querySelector('.cartContainer');
      const wishlistButton = productCTA?.querySelector('.backupWishlistButton.backup-hasVariants');
      const cartModal = this.querySelector('.cartModal');
      const closeButton = cartModal?.querySelector('button.dialogCloseButton');
      
      if (productCTA) {
        if (productCTA._wishlistMouseEnterHandler) {
          productCTA.removeEventListener('mouseenter', productCTA._wishlistMouseEnterHandler);
          delete productCTA._wishlistMouseEnterHandler;
        }
        if (productCTA._wishlistMouseLeaveHandler) {
          productCTA.removeEventListener('mouseleave', productCTA._wishlistMouseLeaveHandler);
          delete productCTA._wishlistMouseLeaveHandler;
        }
      }
      
      if (wishlistButton && wishlistButton._wishlistClickHandler) {
        wishlistButton.removeEventListener('click', wishlistButton._wishlistClickHandler);
        delete wishlistButton._wishlistClickHandler;
      }
      
      if (closeButton && closeButton._wishlistCloseHandler) {
        closeButton.removeEventListener('click', closeButton._wishlistCloseHandler);
        delete closeButton._wishlistCloseHandler;
      }
    }

    initializeWishlistState() {
      // Initialize wishlist state checking when Swym is ready
      const checkWishlistState = () => {
        if (window._swat && window._swat.fetchWrtEventTypeET) {
          this.fetchAndUpdateWishlistState();
        }
      };

      // Check immediately if Swym is already loaded
      if (window._swat && window._swat.fetchWrtEventTypeET) {
        this.fetchAndUpdateWishlistState();
      } else {
        // Otherwise, add to Swym callbacks
        if (!window.SwymCallbacks) {
          window.SwymCallbacks = [];
        }
        window.SwymCallbacks.push(checkWishlistState);
      }
    }

    fetchAndUpdateWishlistState() {
      if (!window._swat || !window._swat.fetchWrtEventTypeET) return;

      window._swat.fetchWrtEventTypeET((products) => {
        this.updateWishlistButtonStates(products);
      }, 4); // et 4 stands for wishlist event type
    }

    updateWishlistButtonStates(wishlistProducts) {
      if (!wishlistProducts || !Array.isArray(wishlistProducts)) return;

      // Get all wishlist buttons in this product card
      const allWishlistButtons = this.querySelectorAll('.backup-addToWishlist');
      let anyVariantInWishlist = false;
      
      allWishlistButtons.forEach(button => {
        const variantId = button.dataset.variantId;
        const productId = button.dataset.productId;
        
        if (!variantId || !productId) return;

        // Check if this variant is in the wishlist
        const isInWishlist = wishlistProducts.find(p => {
          return p.empi == productId && p.epi == variantId;
        });

        if (isInWishlist) {
          this.markButtonAsSaved(button);
          anyVariantInWishlist = true;
        } else {
          this.markButtonAsUnsaved(button);
        }
      });

      // Update the main wishlist button SVG if any variant is in wishlist
      const mainWishlistButton = this.querySelector('button.wishlistButton');
      if (mainWishlistButton) {
        const svg = mainWishlistButton.querySelector('svg.wishlist-heart');
        if (svg) {
          if (anyVariantInWishlist) {
            svg.classList.add('is-filled');
            mainWishlistButton.classList.add('is-saved');
          } else {
            svg.classList.remove('is-filled');
            mainWishlistButton.classList.remove('is-saved');
          }
        }
      }
    }

    markButtonAsSaved(button) {
      // Update button appearance to show it's saved
      button.classList.add('is-saved');
      button.setAttribute('disabled', 'true');
      button.setAttribute('aria-label', 'Saved to wishlist');
      
      // Update SVG to show filled state via CSS
      const svg = button.querySelector('svg.wishlist-heart');
      if (svg) {
        svg.classList.add('is-filled');
      }
    }

    markButtonAsUnsaved(button) {
      // Reset button to unsaved state
      button.classList.remove('is-saved');
      button.removeAttribute('disabled');
      button.setAttribute('aria-label', window.accessibilityStrings?.addToWishlist || 'Add to wishlist');
      
      // Update SVG to show outline state via CSS
      const svg = button.querySelector('svg.wishlist-heart');
      if (svg) {
        svg.classList.remove('is-filled');
      }
    }

    openSharedWishlistModal() {
      const sharedModal = document.querySelector('.shared-wishlist-modal');
      if (!sharedModal || !this.productData) return;

      // Populate modal with product data
      this.populateSharedModal(sharedModal, this.productData);

      // Show modal
      sharedModal.classList.add('open');
      sharedModal.querySelector('.wishDialog')?.classList.add('open');
      document.body.classList.add('overflow-hidden');

      // Setup close button listener (one-time)
      const closeButton = sharedModal.querySelector('button.dialogCloseButton');
      if (closeButton && !closeButton._sharedCloseHandler) {
        closeButton._sharedCloseHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.closeSharedWishlistModal();
        };
        closeButton.addEventListener('click', closeButton._sharedCloseHandler);
      }
    }

    closeSharedWishlistModal() {
      const sharedModal = document.querySelector('.shared-wishlist-modal');
      if (sharedModal) {
        sharedModal.classList.remove('open');
        sharedModal.querySelector('.wishDialog')?.classList.remove('open');
        document.body.classList.remove('overflow-hidden');
      }
    }

    populateSharedModal(modal, productData) {
      // Update image
      const modalImage = modal.querySelector('.shared-modal-image');
      if (modalImage && productData.image) {
        modalImage.src = productData.image;
        modalImage.srcset = productData.image;
      }

      // Update title
      const titleParts = productData.title ? productData.title.split(' - ') : [];
      const titleElement = modal.querySelector('.dialogItemTitle');
      const subtitleElement = modal.querySelector('.dialogItemSubtitle');
      
      if (titleElement) {
        titleElement.textContent = titleParts[0] || '';
      }
      if (subtitleElement) {
        subtitleElement.textContent = titleParts[1] || '';
      }

      // Update price (if we can access it)
      const priceContainer = modal.querySelector('.shared-modal-price');
      if (priceContainer && productData.price) {
        priceContainer.innerHTML = `<span class="price">${this.formatPrice(productData.price)}</span>`;
      }

      // Populate variants
      const variantsContainer = modal.querySelector('.shared-modal-variants');
      if (variantsContainer && productData.variants) {
        variantsContainer.innerHTML = '';
        productData.variants.forEach(variant => {
          const button = document.createElement('button');
          button.className = 'dialogListItem backup-addToWishlist';
          button.setAttribute('data-variant-id', variant.id);
          button.setAttribute('data-product-id', variant.productId);
          button.setAttribute('data-handle', variant.handle);
          button.setAttribute('data-product-title', variant.productTitle);
          button.setAttribute('data-image', variant.image);
          button.setAttribute('data-price', variant.price);
          button.setAttribute('data-variant-title', variant.title);
          button.textContent = variant.title === 'Default Title' ? 'Add to wishlist' : variant.title;
          
          // Add click handler
          button.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.addToWishlist(button.dataset, button);
          });
          
          variantsContainer.appendChild(button);
        });
      }
    }

    formatPrice(cents) {
      // Simple price formatter - adjust based on your currency format
      const dollars = (parseInt(cents) / 100).toFixed(2);
      return `$${dollars}`;
    }

    openWishlistModal() {
      const wishModal = this.querySelector('.wishModal');
      if (wishModal) {
        wishModal.classList.add('open');
        wishModal.querySelector('.wishDialog')?.classList.add('open');
        document.body.classList.add('overflow-hidden');
      }
    }

    closeWishlistModal() {
      const wishModal = this.querySelector('.wishModal');
      if (wishModal) {
        wishModal.classList.remove('open');
        wishModal.querySelector('.wishDialog')?.classList.remove('open');
        document.body.classList.remove('overflow-hidden');
      }
    }

    handleAddToCart(event) {
      // console.log('handleAddToCart', event);
      // Check if the clicked element or its closest parent is a backup-addToCart button
      const button = event.target.closest('button.backup-addToCart');
      if (button) {
        event.preventDefault();
        event.stopPropagation();
        const variantId = button.dataset.variantId;
        if (variantId) {
          this.addToCart(variantId, button);
        }
      }
    }

    handleWishlistClick(event) {
      const button = event.target.closest('button.backup-addToWishlist');
      if (button) {
        event.preventDefault();
        event.stopPropagation();
        this.addToWishlist(button.dataset, button);
      }
    }

    async addToCart(variantId, element) {
      console.log('addToCart', variantId, element);
      if (!variantId || !element) return;

      try {
        element.classList.add('loading');
        element.setAttribute('aria-busy', 'true');
        
        // Use fetchConfig utility for consistent configuration
        const config = fetchConfig();
        config.body = JSON.stringify({
          id: variantId,
          quantity: 1,
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname,
          properties: {}
        });

        const response = await fetch(window.routes.cart_add_url, config);
        const json = await response.json();

        if (!response.ok || json.status) {
          // Handle errors similar to product-form.js
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'list-card-product',
            productVariantId: variantId,
            errors: json.errors || json.description,
            message: json.message || 'Failed to add item to cart',
          });
          
          throw new Error(json.description || json.message || `HTTP error! status: ${response.status}`);
        }

        // Render sections and publish cart update event
        this.renderSections(json);
        publish(PUB_SUB_EVENTS.cartUpdate, { 
          source: 'list-card-product', 
          cartData: json,
          productVariantId: variantId
        });

        // Close modal
        const cartModal = element.closest('.list-card-product')?.querySelector('.cartModal');
        if (cartModal) {
          cartModal.classList.remove('open');
        }
        
        if (document.body.classList.contains('overflow-hidden')) {
          document.body.classList.remove('overflow-hidden');
        }

        this.triggerAutoFunctions();
        this.showSuccess('Item added to cart successfully!');

      } catch (error) {
        console.error('Error adding to cart:', error);
        this.showError(error.message || 'Failed to add item to cart. Please try again.');
      } finally {
        element.classList.remove('loading');
        element.setAttribute('aria-busy', 'false');
      }
    }

    getSectionsToRender() {
      return [
        {
          id: "cart-notification-products",
          section: "cart-notification-products",
          selector: `[id="cart-notification-product-${this.productId}"]`,
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__inner',
        },
        {
          id: "cart-notifications-wrapper",
          section: "cart-notifications",
        },
        {
          id: "mini-cart-upsell",
          section: "mini-cart-upsell",
          selector: `[id="mini-cart-upsell-${this.productId}"]`
        }
      ];
    }

    renderSections(parsedState) {
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = document.getElementById(section.id);
        if (!sectionElement) return;

        if (section.section === 'cart-drawer') {
          sectionElement.closest('cart-drawer')?.classList.toggle('is-empty', parsedState.items.length === 0);
        }
        
        const elementToReplace = 
          sectionElement && sectionElement.querySelector(section.selector)
            ? sectionElement.querySelector(section.selector)
            : sectionElement;
            
        if (elementToReplace && parsedState.sections && parsedState.sections[section.section]) {
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        }
      });

      // Also use cart notification if available
      if (this.cartNotification) {
        this.cartNotification.renderContents(parsedState);
      }
    }

    async addToWishlist(dataset, element) {
      if (!dataset || !element) return;

      const data = {
        epi: parseInt(dataset.variantId),
        empi: parseInt(dataset.productId),
        du: `${window.location.origin}/products/${dataset.handle}`,
        dt: dataset.productTitle,
        iu: dataset.image,
        pr: Number(dataset.price),
        vi: dataset.variantTitle,
      };

      try {
        // const wishlistButton = element.closest('.wishContainer')?.querySelector('.wishlistButton');
        const wishlistButton = element.closest('.card-product')?.querySelector('.wishlistButton');
        if (wishlistButton) {
          wishlistButton.classList.add('loading');
          wishlistButton.setAttribute('aria-busy', 'true');
        }

        if (window._swat && window._swat.addToWishList) {
          await new Promise((resolve, reject) => {
            window._swat.addToWishList(data, (success) => {
              if (success) {
                resolve();
              } else {
                reject(new Error('Wishlist API returned false'));
              }
            });
          });
        } else {
          throw new Error('Wishlist functionality not available');
        }

        // Refresh wishlist state to update all button states
        this.fetchAndUpdateWishlistState();

        this.showSuccess('Item added to wishlist successfully!');

      } catch (error) {
        console.error('Error adding to wishlist:', error);
        this.showError('Failed to add item to wishlist. Please try again.');
      } finally {
        const loadingElements = document.querySelectorAll('.wishContainer .loading');
        loadingElements.forEach(el => {
          el.classList.remove('loading');
          el.setAttribute('aria-busy', 'false');
        });
        
        // Close the local wishlist modal if open
        this.closeWishlistModal();
        
        // Close the shared wishlist modal if open
        this.closeSharedWishlistModal();
        
        // Also close the cart modal if it's being used for wishlist (coming soon products)
        const cartModal = this.querySelector('.cartModal');
        if (cartModal && cartModal.classList.contains('open')) {
          cartModal.classList.remove('open');
          document.body.classList.remove('overflow-hidden');
        }
      }
    }

    triggerAutoFunctions() {
      // Trigger auto gift functionality
      if (typeof window.runAutoGift === 'function') {
        window.runAutoGift();
      }

      // Trigger auto donation functionality  
      if (typeof window.runAutoDonation === 'function') {
        window.runAutoDonation();
      }
    }

    showSuccess(message) {
      this.showNotification(message, 'success');
    }

    showError(message) {
      this.showNotification(message, 'error');
    }

    showNotification(message, type = 'error') {
      const className = `list-card-${type}-notification`;
      let notification = document.querySelector(`.${className}`);
      
      if (!notification) {
        notification = document.createElement('div');
        notification.className = className;
        notification.setAttribute('role', 'alert');
        notification.setAttribute('aria-live', 'polite');
        notification.style.cssText = `
          position: fixed;
          top: 20px;
          right: 20px;
          background: ${type === 'success' ? '#28a745' : '#dc3545'};
          color: white;
          padding: 12px 16px;
          border-radius: 4px;
          z-index: 9999;
          font-size: 14px;
          max-width: 300px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          transform: translateX(100%);
          transition: transform 0.3s ease;
        `;
        document.body.appendChild(notification);
      }
      
      notification.textContent = message;
      notification.style.transform = 'translateX(0)';
      
      // Clear any existing timeout
      if (notification._timeout) {
        clearTimeout(notification._timeout);
      }
      
      notification._timeout = setTimeout(() => {
        notification.style.transform = 'translateX(100%)';
        setTimeout(() => {
          if (notification && notification.parentNode) {
            notification.parentNode.removeChild(notification);
          }
        }, 300);
      }, 5000);
    }

    onClickAway(event) {
      const detailsElement = this.querySelector("details");
      const summaryElement = this.querySelector("summary");
      if (summaryElement && !summaryElement.contains(event.target)) {
        detailsElement?.removeAttribute("open");
      }
    }

    onChange(event) {
      this.querySelectorAll(".list-card-product__link").forEach((anchor) => {
        const url = new URL(anchor.href);
        url.searchParams.set("variant", event.currentTarget.value);
        anchor.href = url.toString();
      });
      
      this.productVariant = event.currentTarget.value;
      
      const listCardProduct = this.querySelector('.list-card-product');
      if (listCardProduct) {
        listCardProduct.classList.add("selected");
        const hideInput = listCardProduct.querySelector('.hide-input');
        if (hideInput) {
          hideInput.hidden = true;
        }
      }
    }

    onClick(event) {
      event.preventDefault();
      this.loadProduct(event.currentTarget.href);
    }

    async loadProduct(url) {
      try {
        const sectionName = "list-card-product";
        const response = await fetch(`${url}?sections=${sectionName}`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const json = await response.json();

        if (json[sectionName]) {
          this.innerHTML = this.getSectionInnerHTML(json[sectionName], sectionName);
          
          // Re-initialize all functionality
          this.bindEvents();
          this.addAccessibilityAttributes();
          this.initializeVariantHandling();
          this.initializeWishlistHandling();
          this.initializeWishlistButtonHandling();
          this.initializeWishlistState();
          
          const listCardProduct = this.querySelector('.list-card-product');
          if (listCardProduct) {
            listCardProduct.classList.add("selected");
            const hideInput = listCardProduct.querySelector('.hide-input');
            if (hideInput) {
              hideInput.hidden = true;
            }
            
            const select = listCardProduct.querySelector('select');
            const option = select?.querySelector(`option[value="${this.productVariant}"]`);
            if (option) {
              option.selected = true;
            }
          }
          
          // Trigger polyfills and other scripts
          if (window.lazyloadPolyfill) {
            window.lazyloadPolyfill(this);
          }
          if (window.convertCurrency) {
            window.convertCurrency(this);
          }
        }
      } catch (error) {
        console.error('Error loading product:', error);
        this.showError('Failed to load product. Please try again.');
      }
    }

    getSectionInnerHTML(html, selector) {
      const DOM = new DOMParser().parseFromString(html, "text/html");
      return DOM.querySelector(selector)?.innerHTML || '';
    }
  });

  // Initialize backup wishlist button handlers for single variants
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.backup-wishListButton').forEach((variantButton) => {
      variantButton.addEventListener('click', function(e) {
        e.preventDefault();
        const variantId = this.getAttribute('data-variant-id');
        const dialogItem = document.querySelector(`.dialogListItem[data-variant-id="${variantId}"]`);
        if (dialogItem) {
          dialogItem.click();
        }
      });
    });
  });
}
